
import React from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { EmailPageLayout } from '@/components/admin/settings/email-editor/EmailPageLayout';
import { StableTabContainer } from '@/components/admin/settings/email-editor/StableTabContainer';
import { TabContentSkeleton } from '@/components/admin/settings/email-editor/TabContentSkeleton';
import { toast } from 'sonner';
import { useTemplateEditorPage } from '@/hooks/useTemplateEditorPage';
import { MissingTemplateError } from '@/components/admin/settings/email-editor/MissingTemplateError';
import { AuthError } from '@/components/admin/settings/email-editor/AuthError';
import { LoadError } from '@/components/admin/settings/email-editor/LoadError';
import { TemplateEditorContent } from '@/components/admin/settings/email-editor/TemplateEditorContent';

export default function EmailTemplateEditor() {
  const { id } = useParams<{ id: string }>();
  
  const {
    isLoaded,
    hasError,
    errorMessage,
    contentMounted,
    isRetrying,
    isAnimating,
    authChecked,
    isAuthenticated,
    handleBack,
    handleSave,
    handleRetry
  } = useTemplateEditorPage(id);
  
  // Show success message when saving
  const handleSaveWithNotification = () => {
    handleSave();
    toast.success("Template saved successfully");
  };

  // Handle missing ID parameter
  if (authChecked && !id) {
    return (
      <AdminLayout>
        <div className="fixed-layout-container w-full" style={{ position: 'relative' }}>
          <EmailPageLayout>
            <MissingTemplateError />
          </EmailPageLayout>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="fixed-layout-container w-full" style={{ position: 'relative' }}>
        <EmailPageLayout>
          <div 
            className="email-editor-content transition-opacity duration-300 ease-in-out w-full"
            style={{
              height: '800px',
              width: '100%',
              position: 'relative',
              opacity: isAnimating ? 0.7 : 1,
            }}
          >
            <StableTabContainer 
              className="email-templates-container" 
              style={{ 
                height: '800px',
                width: '100%',
                display: 'flex', 
                flexDirection: 'column',
              }}
            >
              {!authChecked ? (
                <div className="w-full p-4">
                  <TabContentSkeleton />
                </div>
              ) : !isAuthenticated ? (
                <div className="w-full p-4">
                  <AuthError />
                </div>
              ) : !isLoaded ? (
                <div className="w-full p-4">
                  <TabContentSkeleton />
                </div>
              ) : hasError ? (
                <div className="w-full p-4">
                  <LoadError 
                    errorMessage={errorMessage} 
                    onBack={handleBack} 
                    onRetry={handleRetry} 
                    isRetrying={isRetrying}
                  />
                </div>
              ) : (
                contentMounted && id && isAuthenticated && (
                  <TemplateEditorContent
                    templateId={id}
                    onBack={handleBack}
                    onSave={handleSaveWithNotification}
                  />
                )
              )}
              
              {/* Show skeleton during transition from loaded to contentMounted */}
              {isLoaded && !contentMounted && !hasError && (
                <div className="w-full p-4">
                  <TabContentSkeleton />
                </div>
              )}
            </StableTabContainer>
          </div>
        </EmailPageLayout>
      </div>
    </AdminLayout>
  );
}
